import React, { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import TemplateForm from 'Components/TemplateForm';
import UISpinner from 'Components/UIComponents/UISpinner';
import { useDocumentGuard, useTemplateReplicate } from 'Hooks/Document';
import { Document, DocumentStatuses } from 'Interfaces/Document';
import { AuthorizedRoutePaths } from 'Interfaces/RoutePaths';
import Toast from 'Services/Toast';
import { selectDocument } from 'Utils/selectors';

type TemplateRouteParams = {
  templateId: Document['id'];
};

const TemplateEdit = () => {
  const navigate = useNavigate();
  const params = useParams<TemplateRouteParams>();

  const sourceTemplateId = params.templateId || '';
  const [templateId, setTemplateId] = useState<Document['id'] | undefined>();
  const template = useSelector(state =>
    selectDocument(state, { documentId: templateId }),
  );
  const sourceTemplate = useSelector(state =>
    selectDocument(state, { documentId: sourceTemplateId }),
  );

  const [replicateTemplate, isReplicateLoading] = useTemplateReplicate();

  const handleTemplateCheckFailure = useCallback(() => {
    navigate(AuthorizedRoutePaths.TEMPLATES, { replace: true });
  }, [navigate]);

  const handleTemplateCheckSuccess = useCallback(
    async (template: Document) => {
      try {
        let updatingTemplate = template;

        if (template.status !== DocumentStatuses.DRAFT) {
          updatingTemplate = (await replicateTemplate({
            documentId: sourceTemplateId,
          })) as Document;
        }

        setTemplateId(updatingTemplate.id);
      } catch (error) {
        Toast.handleErrors(error);
        handleTemplateCheckFailure();
      }
    },
    [handleTemplateCheckFailure, replicateTemplate, sourceTemplateId],
  );

  const isCheckingTemplate = useDocumentGuard({
    documentId: sourceTemplateId,
    onFailure: handleTemplateCheckFailure,
    onSuccess: handleTemplateCheckSuccess,
  });

  if (isCheckingTemplate || isReplicateLoading) {
    return <UISpinner wrapperClassName="spinner--main__wrapper" width={50} height={50} />;
  }

  if (!template) return null;

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { fields, ...restTemplate } = template;

  const isApiTemplate = sourceTemplate?.status === DocumentStatuses.API;

  const initialValues = {
    ...restTemplate,
    isApiTemplate,
  };

  return (
    <TemplateForm initialValues={initialValues} sourceTemplateId={sourceTemplateId} />
  );
};

export default TemplateEdit;
